import { useDataProvider, useGetIdentity, usePermissions } from "ra-core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useMemo, type ReactNode } from "react";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import { IS_DEMO } from "../env";
import { DialerApiProvider } from "./api/DialerApiContext";
import { createFakeDialerApi } from "./api/fakeDialerApi";
import { dataProviderStore } from "./api/recordStore";
import { createSupabaseDialerApi } from "./api/supabaseDialerApi";
import { unavailableDialerApi } from "./unavailableDialerApi";

/**
 * One DialerApi for the whole app (mounted by GavionLayout). The fake API writes tables directly,
 * so it only runs in the FakeRest demo build; elsewhere the Supabase RPC API serves the signed-in
 * rep. Without an identity the API reports `dialer_unavailable`.
 */
export const DialerApiRoot = ({
  children,
  isDemo = IS_DEMO,
  getClient = getSupabaseClient,
}: {
  children: ReactNode;
  isDemo?: boolean;
  getClient?: () => SupabaseClient;
}) => {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const me = identity?.id;
  // Separate memos: the Supabase API depends on the rep only, so a permissions or dataProvider
  // change never hands the dialer a new API object (which would re-run its mount and re-claim).
  const supabaseApi = useMemo(
    () =>
      me === undefined || me === null || isDemo
        ? null
        : createSupabaseDialerApi(getClient, Number(me)),
    [me, isDemo, getClient],
  );
  const isAdmin = permissions === "admin";
  const fakeApi = useMemo(
    () =>
      me === undefined || me === null || !isDemo
        ? null
        : createFakeDialerApi({
            store: dataProviderStore(dataProvider),
            me: Number(me),
            isAdmin,
          }),
    [dataProvider, me, isDemo, isAdmin],
  );
  const unavailable = useMemo(() => unavailableDialerApi(), []);
  const api = supabaseApi ?? fakeApi ?? unavailable;
  return <DialerApiProvider api={api}>{children}</DialerApiProvider>;
};
